import React, { useRef } from 'react';
import { Paperclip, Send } from 'lucide-react';
import { Button } from '../ui/button';

export interface V2ChatInputProps {
  value: string;
  onChangeText: (text: string) => void;
  onSend: () => void;
  sendDisabled: boolean;
  onAttachPress: () => void;
  pendingPhotoCount: number;
}

export function V2ChatInput({
  value,
  onChangeText,
  onSend,
  sendDisabled,
  onAttachPress,
  pendingPhotoCount,
}: V2ChatInputProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (!sendDisabled) {
        onSend();
      }
    }
  };

  const handleAttachClick = () => {
    fileInputRef.current?.click();
    onAttachPress();
  };

  return (
    <div className="flex items-end gap-2 px-3 py-2 border-t border-border">
      <div className="relative">
        <Button
          variant="ghost"
          size="icon"
          aria-label="Add attachment"
          onClick={handleAttachClick}
          type="button"
        >
          <Paperclip className="size-5" />
        </Button>
        {pendingPhotoCount > 0 && (
          <span className="absolute -top-1 -right-1 flex items-center justify-center size-5 rounded-full bg-primary text-primary-foreground text-xs font-medium">
            {pendingPhotoCount}
          </span>
        )}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*,.pdf"
          multiple
          className="hidden"
          tabIndex={-1}
        />
      </div>

      <textarea
        className="flex-1 resize-none rounded-md border border-input bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
        rows={1}
        style={{ maxHeight: '6rem', overflowY: 'auto' }}
        value={value}
        onChange={(e) => onChangeText(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder="Type a message..."
        aria-label="Message input"
      />

      <Button
        variant="default"
        size="icon"
        aria-label="Send message"
        disabled={sendDisabled}
        onClick={onSend}
        type="button"
      >
        <Send className="size-5" />
      </Button>
    </div>
  );
}
